import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Dashboard.css';
import { FaUsers, FaFileInvoice, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { Line, Doughnut, Bar,Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import MapCompteurs from '../components/MapCompteurs';
import { API } from '../config/api';

ChartJS.register(LineElement, BarElement, PointElement, LinearScale, CategoryScale, ArcElement, Tooltip, Legend);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [consoData, setConsoData] = useState([]);
  const [facturationData, setFacturationData] = useState({ paye: [], impaye: [] });
  const [contratParSecteur, setContratParSecteur] = useState([]);

  const token = localStorage.getItem('token');
  const currentYear = new Date().getFullYear();
  const earliestYear = 2023;
  const availableYears = Array.from(
    { length: Math.max(currentYear - earliestYear + 1, 1) },
    (_, i) => currentYear - i
  );

  useEffect(() => {
  fetchStats();
  fetchConsommation();
  fetchFacturation();
  fetchContratsParSecteur();
}, [year]);

const fetchContratsParSecteur = async () => {
  try {
    const res = await axios.get(API+'/api/dashboard/contrats-par-secteur', {
      headers: { Authorization: `Bearer ${token}` }
    });
    setContratParSecteur(res.data);
  } catch (err) {
    console.error('Erreur chargement contrats secteur:', err);
  }
};


  const fetchStats = async () => {
    try {
      const res = await axios.get(API+'/api/dashboard/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📊 Stats reçues:', res.data);
      setStats(res.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const fetchConsommation = async () => {
    try {
      const res = await axios.get(API+`/api/dashboard/consommation?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsoData(res.data.monthly);
    } catch (err) {
      console.error('Erreur chargement conso:', err);
    }
  };

  const fetchFacturation = async () => {
  try {
    const res = await axios.get(API+`/api/dashboard/facturation?year=${year}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    console.log("📊 Données facturation reçues:", res.data);

    const formatted = formatFacturationMensuelle(res.data);
    setFacturationData(formatted);
  } catch (err) {
    console.error('Erreur chargement facturation:', err);
  }
};


  if (!stats) return <div className="dashboard">Chargement...</div>;

  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

  function formatFacturationMensuelle(rawData) {
  const paye = new Array(12).fill(0);
  const impaye = new Array(12).fill(0);

  rawData.forEach(entry => {
    const monthIndex = parseInt(entry.mois) - 1;
    const montant = parseFloat(entry.total || 0);
    const status = (entry.status || "").toLowerCase().trim();

    if (['payé', 'paye', 'payée', 'payee'].includes(status)) {
      paye[monthIndex] += montant;
    } else {
      // Any other status (including impaye, impayé, impayée, null, empty) counts as unpaid
      impaye[monthIndex] += montant;
    }
  });

  return { paye, impaye };
}


  const lineChartData = {
    labels: months,
    datasets: [{
      label: 'Consommation (m³)',
      data: consoData,
      fill: false,
      borderColor: 'blue',
      backgroundColor: 'blue',
      tension: 0.3
    }]
  };

  const doughnutChartData = {
    labels: ['Payé', 'Impayé'],
    datasets: [{
      data: [stats.montantPaye || 0, stats.montantImpayes || 0],
      backgroundColor: ['#4CAF50', '#F44336']
    }]
  };

  const barChartData = {
    labels: months,
    datasets: [
      {
        label: 'Payé (DH)',
        data: facturationData.paye,
        backgroundColor: 'rgba(76, 175, 80, 0.6)'
      },
      {
        label: 'Impayé (DH)',
        data: facturationData.impaye,
        backgroundColor: 'rgba(244, 67, 54, 0.6)'
      }
    ]
  };

  /* const barChartDataFact = {
  labels: months,
  datasets: [
    {
      label: 'Payé (DH)',
      data: facturationData.paye,
      backgroundColor: 'rgba(76, 175, 80, 0.6)'
    },
    {
      label: 'Impayé (DH)',
      data: facturationData.impaye,
      backgroundColor: 'rgba(244, 67, 54, 0.6)'
    }
  ]
}; */
// 🔒 Protège contre les appels avant que les données arrivent
const sortedData = [...contratParSecteur].sort((a, b) => b.total - a.total);
const top5 = sortedData.slice(0, 5);

const totalContrats = top5.reduce((sum, s) => sum + s.total, 0);

const pieChartData = {
  labels: top5.map(item => item.secteur),
  datasets: [
    {
      data: top5.map(item => item.total),
      backgroundColor: ['#4CAF50', '#03A9F4', '#FFC107', '#E91E63', '#9C27B0'],
      borderWidth: 1,
      hoverOffset: 10
    }
  ]
};

const pieChartOptions = {
  plugins: {
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const percent = totalContrats > 0 ? ((value / totalContrats) * 100).toFixed(1) : 0;
          return `${context.label}: ${value} contrats (${percent}%)`;
        }
      }
    },
    legend: {
      position: 'right',
      labels: {
        boxWidth: 15,
        padding: 10
      }
    }
  }
};



  return (
    <div className="dashboard">
      <h2>Tableau de Bord</h2>

      <div className="dashboard-cards">
        <div className="card blue"><FaUsers className="icon" /><div><h3>{stats.totalClients || 0}</h3><p>Total Clients</p></div></div>
        <div className="card green"><FaFileInvoice className="icon" /><div><h3>{stats.totalContracts || 0}</h3><p>Contrats Actifs</p></div></div>
        <div className="card green"><FaCheckCircle className="icon" /><div><h3>{stats.facturesPayees || 0}</h3><p>Factures Payées</p></div></div>
        <div className="card red"><FaTimesCircle className="icon" /><div><h3>{stats.facturesImp || 0}</h3><p>Factures Impayées</p></div></div>
        <div className="card"><div className="icon total">💧</div><div><h3>{stats.totalConsommation || 0} m³</h3><p>Consommation Totale</p></div></div>
      </div>

      <div className="charts-section">
        <div className="chart-card">
          <div className="chart-header">
            <h4>Évolution de la Consommation ({year})</h4>
            <select onChange={e => setYear(Number(e.target.value))} value={year}>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <Line data={lineChartData} />
        </div>

        

        <div className="chart-card">
          <h4>Évolution Montant Facturé ({year})</h4>
          <Bar data={barChartData} />
        </div>

        
      </div>
      <div className='charts-section'>
        

      </div>
      <MapCompteurs />

      <footer className="dashboard-footer">
        <p>© 2025 Abar. Powered by <strong>Jysser Connect</strong></p>
      </footer>

    </div>
  );
}

